import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { taxonomiesApi } from '../api/taxonomiesApi'
import type { Taxonomy, TaxonomyTerm } from '../model/taxonomy'

export interface TaxonomiesState {
  taxonomies: Taxonomy[]
  taxonomiesBySubjectId: Record<number, Taxonomy[]>
  selectedSubjectId: number | null
  selectedTaxonomyId: number | null
  termsByTaxonomyId: Record<number, TaxonomyTerm[]>
  allTerms: TaxonomyTerm[]
  allTermsLoaded: boolean
  search: string
  status: 'idle' | 'loading' | 'ready' | 'error'
  error: string | null
}

const initialState: TaxonomiesState = {
  taxonomies: [],
  taxonomiesBySubjectId: {},
  selectedSubjectId: null,
  selectedTaxonomyId: null,
  termsByTaxonomyId: {},
  allTerms: [],
  allTermsLoaded: false,
  search: '',
  status: 'idle',
  error: null,
}

export const loadTaxonomies = createAsyncThunk(
  'taxonomies/load',
  async (input: { subjectId: number; force?: boolean }, { getState }) => {
    const state = (getState() as { taxonomies?: TaxonomiesState }).taxonomies
    if (!input.force && state?.taxonomiesBySubjectId[input.subjectId]) {
      return { subjectId: input.subjectId, taxonomies: state.taxonomiesBySubjectId[input.subjectId] }
    }
    const taxonomies = await taxonomiesApi.listTaxonomies(input.subjectId)
    return { subjectId: input.subjectId, taxonomies }
  }
)

export const loadTerms = createAsyncThunk(
  'taxonomies/loadTerms',
  async (arg: number | { taxonomyId?: number; force?: boolean }, { getState }) => {
    const taxonomyId = typeof arg === 'number' ? arg : arg.taxonomyId
    const force = typeof arg === 'object' ? Boolean(arg.force) : false
    const state = (getState() as { taxonomies?: TaxonomiesState }).taxonomies

    if (taxonomyId) {
      if (!force && state?.termsByTaxonomyId[taxonomyId]?.length) {
        return { taxonomyId, terms: state.termsByTaxonomyId[taxonomyId] }
      }
      const terms = await taxonomiesApi.listTerms(taxonomyId)
      return { taxonomyId, terms }
    }

    if (!force && state?.allTermsLoaded) {
      return { taxonomyId: undefined, terms: state.allTerms }
    }
    const terms = await taxonomiesApi.listTerms()
    return { taxonomyId: undefined, terms }
  }
)

export const taxonomiesSlice = createSlice({
  name: 'taxonomies',
  initialState,
  reducers: {
    selectSubject(state, action: PayloadAction<number | null>) {
      state.selectedSubjectId = action.payload
      state.selectedTaxonomyId = null
      state.search = ''
      state.taxonomies = action.payload ? (state.taxonomiesBySubjectId[action.payload] ?? []) : []
    },
    setSearch(state, action: PayloadAction<string>) {
      state.search = action.payload
    },
    selectTaxonomy(state, action: PayloadAction<number | null>) {
      state.selectedTaxonomyId = action.payload
    },
    upsertTaxonomy(state, action: PayloadAction<Taxonomy>) {
      const index = state.taxonomies.findIndex((item) => item.id === action.payload.id)
      if (index >= 0) {
        state.taxonomies[index] = action.payload
      } else {
        state.taxonomies.push(action.payload)
      }
      const list = state.taxonomiesBySubjectId[action.payload.subject_id] ?? []
      const subjectIndex = list.findIndex(item => item.id === action.payload.id)
      if (subjectIndex >= 0) list[subjectIndex] = action.payload
      else list.push(action.payload)
      state.taxonomiesBySubjectId[action.payload.subject_id] = list
    },
    removeTaxonomy(state, action: PayloadAction<number>) {
      state.taxonomies = state.taxonomies.filter((item) => item.id !== action.payload)
      Object.keys(state.taxonomiesBySubjectId).forEach(subjectId => {
        state.taxonomiesBySubjectId[Number(subjectId)] = state.taxonomiesBySubjectId[Number(subjectId)].filter(item => item.id !== action.payload)
      })
      delete state.termsByTaxonomyId[action.payload]
      state.allTerms = state.allTerms.filter((item) => item.taxonomy_id !== action.payload)
      if (state.selectedTaxonomyId === action.payload) {
        state.selectedTaxonomyId = state.taxonomies[0]?.id ?? null
      }
    },
    upsertTerm(state, action: PayloadAction<TaxonomyTerm>) {
      const term = action.payload
      const taxId = term.taxonomy_id

      // Update termsByTaxonomyId
      if (!state.termsByTaxonomyId[taxId]) {
        state.termsByTaxonomyId[taxId] = []
      }
      const list = state.termsByTaxonomyId[taxId]
      const index = list.findIndex((item) => item.id === term.id)
      if (index >= 0) {
        list[index] = term
      } else {
        list.push(term)
      }

      // Update allTerms
      const allIndex = state.allTerms.findIndex((item) => item.id === term.id)
      if (allIndex >= 0) {
        state.allTerms[allIndex] = term
      } else {
        state.allTerms.push(term)
      }

      // Update terms_count in taxonomy
      const tax = state.taxonomies.find((item) => item.id === taxId)
      if (tax && index < 0) {
        tax.terms_count = (tax.terms_count || 0) + 1
      }
    },
    removeTerm(state, action: PayloadAction<{ id: number; taxonomyId: number }>) {
      const { id, taxonomyId } = action.payload
      if (state.termsByTaxonomyId[taxonomyId]) {
        state.termsByTaxonomyId[taxonomyId] = state.termsByTaxonomyId[taxonomyId].filter((item) => item.id !== id)
      }
      state.allTerms = state.allTerms.filter((item) => item.id !== id)
      const tax = state.taxonomies.find((item) => item.id === taxonomyId)
      if (tax && tax.terms_count) {
        tax.terms_count = Math.max(0, tax.terms_count - 1)
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadTaxonomies.pending, (state) => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(loadTaxonomies.fulfilled, (state, action) => {
        state.status = 'ready'
        state.taxonomiesBySubjectId[action.payload.subjectId] = action.payload.taxonomies
        if (state.selectedSubjectId === action.payload.subjectId) state.taxonomies = action.payload.taxonomies
        if (state.selectedSubjectId === action.payload.subjectId && state.selectedTaxonomyId === null && action.payload.taxonomies.length > 0) {
          state.selectedTaxonomyId = action.payload.taxonomies[0].id
        }
      })
      .addCase(loadTaxonomies.rejected, (state, action) => {
        state.status = 'error'
        state.error = action.error.message || 'Không thể tải danh sách taxonomy'
      })
      .addCase(loadTerms.fulfilled, (state, action) => {
        const { taxonomyId, terms } = action.payload
        if (taxonomyId) {
          state.termsByTaxonomyId[taxonomyId] = terms
        } else {
          state.allTerms = terms
          state.allTermsLoaded = true
        }
      })
  },
})

export const taxonomiesActions = taxonomiesSlice.actions
export const taxonomiesReducer = taxonomiesSlice.reducer
